import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useLoaderData, useNavigate, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import type { ZodIssue } from 'zod';
import { Avatar, Button, Input, Label, Modal } from '@immo/ui-kit';
import {
  MEMBER_AVATAR_ALLOWED_CONTENT_TYPES,
  UpdateAdminMemberBodySchema,
  type GetAdminMemberResponse,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { memberInitials } from '@/lib/member-initials';
import { isAllowedPhoto, PhotoPolicyError, uploadMemberPhoto } from '@/lib/media/upload-member-photo';

type Field = 'email' | 'first-name' | 'last-name' | 'position' | 'phone' | 'mobile';

type FormErrors = Readonly<Record<Field, string | null>>;

const NO_ERRORS: FormErrors = {
  email: null,
  'first-name': null,
  'last-name': null,
  position: null,
  phone: null,
  mobile: null,
};

type PhotoState =
  | { readonly kind: 'stored' }
  | { readonly kind: 'uploading' }
  | { readonly kind: 'uploaded'; readonly fileId: string; readonly previewUrl: string }
  | { readonly kind: 'removed' };

function photoChange(photo: PhotoState): { avatarMediaId?: string | null } {
  if (photo.kind === 'uploaded') return { avatarMediaId: photo.fileId };

  if (photo.kind === 'removed') return { avatarMediaId: null };

  return {};
}

function fieldFromPath(path: ReadonlyArray<PropertyKey>): Field | undefined {
  const head = path[0];

  if (head === 'email') return 'email';

  if (head === 'firstName') return 'first-name';

  if (head === 'lastName') return 'last-name';

  if (head === 'position' || head === 'phone' || head === 'mobile') return head;

  return undefined;
}

function issueKey(issue: ZodIssue): string {
  if (issue.code === 'too_small') return 'required';

  if (issue.code === 'too_big') return 'too-long';

  return 'invalid';
}

export async function memberEditLoader({ params }: LoaderFunctionArgs): Promise<GetAdminMemberResponse> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing member id', { status: 404 });
  }

  const result = await apiClient.adminMembers.get({ pathParams: { id } });

  if (!result.ok) {
    throw new Response('Failed to load member', { status: result.status });
  }

  return result.body;
}

export function MemberEditPage() {
  const t = useTranslations('pages.admin.members.edit');
  const tErrors = useTranslations('errors');
  const member = useLoaderData() as GetAdminMemberResponse;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [email, setEmail] = useState(member.email);
  const [firstName, setFirstName] = useState(member.firstName);
  const [lastName, setLastName] = useState(member.lastName);
  const [position, setPosition] = useState(member.position ?? '');
  const [phone, setPhone] = useState(member.phone ?? '');
  const [mobile, setMobile] = useState(member.mobile ?? '');
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState<PhotoState>({ kind: 'stored' });
  const [photoError, setPhotoError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fullName = `${member.firstName} ${member.lastName}`.trim();
  const heading = fullName === '' ? member.email : fullName;
  const previewSrc =
    photo.kind === 'uploaded'
      ? photo.previewUrl
      : photo.kind === 'removed'
        ? undefined
        : (member.avatarUrl ?? undefined);
  const hasPhoto = previewSrc !== undefined;
  const photoBusy = submitting || photo.kind === 'uploading';

  useEffect(() => {
    if (photo.kind !== 'uploaded') return;

    const url = photo.previewUrl;

    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (file === undefined) return;

    setPhotoError(null);

    const before = photo;

    if (!isAllowedPhoto(file)) {
      setPhotoError(tErrors('identity.member-profile.invalid-photo'));

      return;
    }

    setPhoto({ kind: 'uploading' });

    try {
      const fileId = await uploadMemberPhoto(member.id, file);
      setPhoto({ kind: 'uploaded', fileId, previewUrl: URL.createObjectURL(file) });
    } catch (error) {
      setPhoto(before);
      setPhotoError(
        error instanceof PhotoPolicyError ? tErrors('identity.member-profile.invalid-photo') : t('photo.upload-error'),
      );
    }
  };

  const onRemove = () => {
    setPhotoError(null);
    setPhoto(photo.kind === 'uploaded' ? { kind: 'stored' } : { kind: 'removed' });
  };

  const clearError = (key: Field) => setErrors((prev) => (prev[key] === null ? prev : { ...prev, [key]: null }));

  const close = () => navigate(`/workspaces/members/${member.id}`);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenericError(null);

    const changed = {
      ...(position !== (member.position ?? '') ? { position } : {}),
      ...(phone !== (member.phone ?? '') ? { phone } : {}),
      ...(mobile !== (member.mobile ?? '') ? { mobile } : {}),
    };
    const parsed = UpdateAdminMemberBodySchema.safeParse({
      email,
      firstName,
      lastName,
      ...changed,
      ...photoChange(photo),
    });

    if (!parsed.success) {
      const next: Record<Field, string | null> = { ...NO_ERRORS };

      for (const issue of parsed.error.issues) {
        const field = fieldFromPath(issue.path);

        if (field !== undefined && next[field] === null) {
          const key = field === 'email' && email.trim().length === 0 ? 'required' : issueKey(issue);
          next[field] = t(`errors.${field}.${key}`);
        }
      }

      setErrors(next);

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminMembers.update({
        pathParams: { id: member.id },
        body: parsed.data,
      });

      if (result.ok) {
        await revalidator.revalidate();
        navigate(`/workspaces/members/${member.id}`);

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;

      if (code === 'identity.member-profile.invalid-photo') {
        setPhotoError(tErrors(code));

        return;
      }

      setGenericError(t(result.status === 403 ? 'forbidden-error' : 'generic-error'));
    } catch {
      setGenericError(t('generic-error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={close}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
            {t('back-cta')}
          </Button>
          <Button type="submit" form="member-edit-form" disabled={photoBusy}>
            {submitting ? t('submit-loading') : t('submit-cta')}
          </Button>
        </>
      }
    >
      <form id="member-edit-form" className="space-y-4" onSubmit={submit} noValidate>
        <div className="space-y-1">
          <Label htmlFor="photo">{t('photo.label')}</Label>
          <div className="flex items-center gap-3">
            <Avatar
              className="h-12 w-12 text-lg"
              initials={memberInitials(heading)}
              seed={member.id}
              src={previewSrc}
            />
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={photoBusy}
              onClick={() => inputRef.current?.click()}
            >
              {t(hasPhoto ? 'photo.replace-cta' : 'photo.upload-cta')}
            </Button>
            {(photo.kind === 'uploaded' || (photo.kind === 'stored' && member.avatarUrl !== null)) && (
              <Button variant="ghost" size="sm" type="button" disabled={photoBusy} onClick={onRemove}>
                {t('photo.remove-cta')}
              </Button>
            )}
            <input
              ref={inputRef}
              id="photo"
              type="file"
              className="hidden"
              accept={MEMBER_AVATAR_ALLOWED_CONTENT_TYPES.join(',')}
              onChange={(e) => void onPick(e)}
              disabled={photoBusy}
            />
          </div>
          {photo.kind === 'uploading' && <p className="text-sm text-text-tertiary">{t('photo.uploading')}</p>}
          {photoError !== null && <p className="text-sm text-danger-600">{photoError}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="email">{t('email-label')}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearError('email');
            }}
            disabled={submitting}
            invalid={errors.email !== null}
          />
          {errors.email !== null && <p className="text-sm text-danger-600">{errors.email}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="first-name">{t('first-name-label')}</Label>
          <Input
            id="first-name"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              clearError('first-name');
            }}
            disabled={submitting}
            invalid={errors['first-name'] !== null}
          />
          {errors['first-name'] !== null && <p className="text-sm text-danger-600">{errors['first-name']}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="last-name">{t('last-name-label')}</Label>
          <Input
            id="last-name"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              clearError('last-name');
            }}
            disabled={submitting}
            invalid={errors['last-name'] !== null}
          />
          {errors['last-name'] !== null && <p className="text-sm text-danger-600">{errors['last-name']}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="position">{t('position-label')}</Label>
          <Input
            id="position"
            value={position}
            onChange={(e) => {
              setPosition(e.target.value);
              clearError('position');
            }}
            disabled={submitting}
            invalid={errors.position !== null}
          />
          {errors.position !== null && <p className="text-sm text-danger-600">{errors.position}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="phone">{t('phone-label')}</Label>
          <Input
            id="phone"
            type="tel"
            placeholder={t('phone-placeholder')}
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              clearError('phone');
            }}
            disabled={submitting}
            invalid={errors.phone !== null}
          />
          {errors.phone !== null && <p className="text-sm text-danger-600">{errors.phone}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="mobile">{t('mobile-label')}</Label>
          <Input
            id="mobile"
            type="tel"
            placeholder={t('mobile-placeholder')}
            value={mobile}
            onChange={(e) => {
              setMobile(e.target.value);
              clearError('mobile');
            }}
            disabled={submitting}
            invalid={errors.mobile !== null}
          />
          {errors.mobile !== null && <p className="text-sm text-danger-600">{errors.mobile}</p>}
        </div>
        {genericError !== null && <p className="text-base text-danger-600">{genericError}</p>}
      </form>
    </Modal>
  );
}
